import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  CircularProgress,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography
} from '@mui/material';
import { post, get } from 'src/services';
import { authenticationService } from 'src/services/authentication.service';

interface AddBillingTaxTypeProps {
  handleClose: () => void;
  fetchData: () => void;
  showSnackbar: (message: string, severity: 'success' | 'error') => void;
}

const AddBillingTaxType: React.FC<AddBillingTaxTypeProps> = ({ handleClose, fetchData, showSnackbar }) => {
  const { vendorId } = authenticationService.getVendorFranchiseIds();
  const [loading, setLoading] = useState(false);
  const [taxesList, setTaxesList] = useState<any[]>([]);
  const [selectedTaxes, setSelectedTaxes] = useState<number[]>([]);
  
  const [formData, setFormData] = useState({
    name: '',
    exclusive: false,
    percentage: true,
    status: false
  });

  // Fetch available taxes
  useEffect(() => {
    fetchTaxes();
  }, []);

  const fetchTaxes = async () => {
    try {
      const response = await get(`${process.env.REACT_APP_BASE_API_URL}/api/tax/vendor/${vendorId}/getAllTaxesByVendor`);
      if (response && response.success && response.data) {
        setTaxesList(response.data);
      } else if (Array.isArray(response)) {
        setTaxesList(response);
      } else {
        setTaxesList([]);
      }
    } catch (error) {
      console.error('Error fetching taxes:', error);
    }
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (name: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [name]: event.target.checked }));
  };

  const handleTaxSelection = (taxId: number) => {
    setSelectedTaxes((prev) =>
      prev.includes(taxId)
        ? prev.filter((id) => id !== taxId)
        : [...prev, taxId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTaxes.length === taxesList.length) {
      setSelectedTaxes([]);
    } else {
      setSelectedTaxes(taxesList.map((tax) => tax.id));
    }
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      showSnackbar('Please enter Name', 'error');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        vendorId: vendorId,
        taxes: selectedTaxes
      };

      const response = await post(`${process.env.REACT_APP_BASE_API_URL}/api/billing-tax-type/create`, payload);
      
      if (response && response.success) {
        showSnackbar('Billing tax type added successfully', 'success');
        fetchData();
        handleClose();
      } else {
        showSnackbar(response?.message || 'Error adding billing tax type', 'error');
      }
    } catch (error) {
      console.error('Error adding:', error);
      showSnackbar('Error adding billing tax type', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={3}>
        {/* Type/Name */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="Type *"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter billing tax type name"
          />
        </Grid>

       
        {/* Exclusive Switch */}
        <Grid item xs={12}>
  <Grid container spacing={2}>
    <Grid item xs={4}>
      <FormControlLabel
        control={
          <Switch
            checked={formData.exclusive}
            onChange={handleSwitchChange('exclusive')}
            name="exclusive"
            color="primary"
          />
        }
        label="Exclusive"
      />
    </Grid>
        <Grid item xs={4}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.percentage}
                            onChange={handleSwitchChange('percentage')}
                            name="percentage"
                            color="primary"
                          />
                        }
                        label="Percentage"
                      />
                    </Grid>
    <Grid item xs={4}>
      <FormControlLabel
        control={
          <Switch
            checked={formData.status}
            onChange={handleSwitchChange('status')}
            name="status"
            color="primary"
          />
        }
        label="Status"
      />
    </Grid>
  </Grid>
</Grid>

       

        {/* Taxes List Section */}
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Taxes List
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedTaxes.length === taxesList.length && taxesList.length > 0}
                    indeterminate={selectedTaxes.length > 0 && selectedTaxes.length < taxesList.length}
                    onChange={handleSelectAll}
                  />
                }
                label="Select All"
              />
            </Box>
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              <List dense>
                {taxesList.map((tax) => (
                  <ListItem key={tax.id} dense>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedTaxes.includes(tax.id)}
                          onChange={() => handleTaxSelection(tax.id)}
                        />
                      }
                      label={`${tax.taxType} (${tax.calculationOn})`}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </Box>
    </Box>
  );
};

export default AddBillingTaxType;